/// <amd-module name="Build/DeletedPipelines/DeletedPipelinesDataService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import * as Resources from "Build/DeletedPipelines/Resources";

import { IGlobalMessagesService } from 'TFS/GlobalBanner/GlobalMessagesService';
import { getTFSPageData } from "Tfs/Platform/Page";

import { DeletedPipelineData, DeletedPipelineDataList, PipelineDeletionRetentionSettings } from "Build/Client/Contracts/DataProvider";
import { IBuildRestClient } from "Build/Client/RestClient/Build";

import { IObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";

export interface IDeletedPipelinesDataService extends IVssService {
    data: IObservableArray<DeletedPipelineData>;
    hasDeletedPipelines: IReadonlyObservableValue<boolean>;
    pipelineDeletionRetentionSettings: IReadonlyObservableValue<PipelineDeletionRetentionSettings>;
    restore(definitionId: number): void;
}

class DeletedPipelinesDataService extends VssService implements IDeletedPipelinesDataService {
    private _data: DeletedPipelineDataList;
    private _observable: ObservableArray<DeletedPipelineData> | undefined;
    private _pipelineDeletionRetentionSettings: IReadonlyObservableValue<PipelineDeletionRetentionSettings>;
    private _hasDeletedPipelines: ObservableValue<boolean>;

    public get data(): IObservableArray<DeletedPipelineData> {
        if (this._observable === undefined) {
            this._observable = new ObservableArray<DeletedPipelineData>(this._data.pipelines);
        }
        return this._observable;
    }

    public restore(definitionId: number): void {
        const buildClient = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const tfsPageData = getTFSPageData(this.pageContext);
        if (!tfsPageData || !tfsPageData.project) {
            return;
        }

        buildClient.restoreDefinition(tfsPageData.project.id, definitionId, false).then(() => {
            const messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
            if (this._observable) {
                for (let i = 0; i < this._observable.length; i++) {
                    if (this._observable.value[i].id === definitionId) {
                        const name = this._observable.value[i].name;
                        this._hasDeletedPipelines.value = this._observable.length > 1;
                        this._observable.splice(i, 1);
                        messageService.addToast({
                            message: format(Resources.RestoredSuccessfully, name),
                            duration: 2000
                        });
                        return;
                    }
                }
            }
        }, () => {
            const messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
            messageService.addToast({
                message: format(Resources.RestoreFailed),
                duration: 2000
            });
        });
    }

    public get hasDeletedPipelines(): IReadonlyObservableValue<boolean> {
        return this._hasDeletedPipelines;
    }

    public get pipelineDeletionRetentionSettings(): IReadonlyObservableValue<PipelineDeletionRetentionSettings> {
        return this._pipelineDeletionRetentionSettings;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    private _initialize() {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<DeletedPipelineDataList>("ms.vss-build-web.deleted-pipelines-data-provider");
        if (data) {
            this._data = data;
        }
        else {
            this._data = { pipelines: [], retentionSettings: { deleteDefinitionJobScheduleDays: 30 } };
        }

        this._pipelineDeletionRetentionSettings = new ObservableValue<PipelineDeletionRetentionSettings>(this._data.retentionSettings);
        this._hasDeletedPipelines = new ObservableValue<boolean>(this._data.pipelines && this._data.pipelines.length > 0);
    }
}

Services.add("IDeletedPipelinesDataService", { serviceFactory: DeletedPipelinesDataService });
